// @flow
import React from "react";
import { Row, Col, Card, CardBody } from "reactstrap";
import {
    Typography,
    Icon,
    EmailReceive,
    Phone,
    AgencyTeamCarousel,
    ReadMore,
} from "@2po-c21/components";
import { t } from "ttag";
import { GatsbyImage } from "gatsby-plugin-image";
import { Section } from "@containers";
import style from "./style.module.scss";
import classNames from "classnames";
import { useIsMobile } from "../../hooks/useIsMobile";

type Props = {
    employees: Array<Object>,
    title: string,
    description: string,
    fallbackImage: string,
    locale?: string,
    hideTeamSection?: boolean,
};

type DescriptionContentProps = {
    description: string,
};

const SHORT_DESCRIPTION_UPPER_LIMIT = 300;
const LONG_DESCRIPTION_LOWER_LIMIT = 500;

const DescriptionContent = ({ description }: DescriptionContentProps) => (
    <Typography type="body">
        {description.split(/\r?\n/).map((line, index) => (
            <span key={index}>
                {line}
                <br />
            </span>
        ))}
    </Typography>
);

const ContactInfo = ({
    email,
    telephone,
}: {
    email: string,
    telephone: string,
}) => (
    <div className={style.cardContact}>
        {email && (
            <a
                href={`mailto:${email}`}
                className={classNames(style.links, style.emailText)}
            >
                <Typography type="body-small">
                    <Icon
                        icon={EmailReceive}
                        variant="white"
                        className={style.icon}
                    />
                    {email}
                </Typography>
            </a>
        )}
        {telephone && (
            <a href={`tel:${telephone}`} className={style.links}>
                <Typography type="body-small">
                    <Icon
                        icon={Phone}
                        variant="relentless-gold"
                        className={style.icon}
                    />
                    {telephone}
                </Typography>
            </a>
        )}
    </div>
);

const AgencyTeam = ({
    employees,
    title,
    description,
    fallbackImage,
    locale,
    hideTeamSection,
}: Props) => {
    const _description = description || "";

    const isMobile = useIsMobile();
    const isLongDescription =
        description.length >= LONG_DESCRIPTION_LOWER_LIMIT;
    const isShortDescription =
        _description.length <= SHORT_DESCRIPTION_UPPER_LIMIT;
    const collapseContent = isMobile && isLongDescription;

    return (
        <Section
            theme="themeGray"
            enableBackground
            className={style.agencyTeam}
            hideBackgroundMobile={true}
        >
            <div className={style.agencyTeamCol}>
                <div>
                    <Typography type="heading-5-bold">{title}</Typography>
                </div>
                {_description && (
                    <div
                        className={classNames(style.descriptionContainer, {
                            [style.descriptionShort]: isShortDescription,
                        })}
                    >
                        {isMobile ? (
                            <ReadMore
                                collapseContent={collapseContent}
                                label={t`common.text.read-more`}
                                overlayColor="ui-light"
                            >
                                <DescriptionContent
                                    description={_description}
                                />
                            </ReadMore>
                        ) : (
                            <DescriptionContent description={_description} />
                        )}
                    </div>
                )}
            </div>
            {!hideTeamSection && employees != null && employees.length > 0 && (
                <Col className={style.employeeWrapper}>
                    <div className={style.background} />
                    <Row className="mb-4">
                        <Typography type="heading-5-bold">
                            {t`agency.employees.title`}
                        </Typography>
                    </Row>
                    <Row className={style.employeeRow}>
                        {employees.map((employee) => (
                            <Col
                                key={employee.id}
                                className={style.cardContainer}
                                md={6}
                            >
                                <Card className={style.card}>
                                    {employee?.image ? (
                                        <GatsbyImage
                                            image={
                                                employee.image.gatsbyImageData
                                            }
                                            objectPosition="top"
                                            alt={`${employee.firstName} ${employee.lastName}`}
                                        />
                                    ) : (
                                        <div className={style.fallbackWrapper}>
                                            {fallbackImage?.imageSrc()}
                                        </div>
                                    )}
                                    <CardBody className={style.cardBody}>
                                        <div className={style.cardInformation}>
                                            <Typography
                                                type="heading-6-bold"
                                                className={style.typographyName}
                                            >
                                                {employee.firstName}{" "}
                                                {employee.lastName}
                                            </Typography>
                                            {employee.functions &&
                                                employee.functions.length >
                                                    0 && (
                                                    <Typography
                                                        type="heading-6-bold"
                                                        className={
                                                            style.typographyFunction
                                                        }
                                                    >
                                                        {employee.functions
                                                            .map((fn) => {
                                                                const functionNames =
                                                                    {
                                                                        nl: fn.functionNameNl,
                                                                        fr: fn.functionNameFr,
                                                                    };
                                                                return functionNames[
                                                                    locale
                                                                ];
                                                            })
                                                            .join(" - ")}
                                                    </Typography>
                                                )}
                                        </div>
                                        <ContactInfo
                                            email={employee.email}
                                            telephone={employee.telephone}
                                        />
                                    </CardBody>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                    <div className="d-lg-none">
                        <AgencyTeamCarousel>
                            {employees.map((employee, index) => (
                                <Card key={index} className={style.card}>
                                    {employee?.image ? (
                                        <GatsbyImage
                                            image={
                                                employee.image.gatsbyImageData
                                            }
                                            objectPosition="top"
                                            alt={`${employee.firstName} ${employee.lastName}`}
                                        />
                                    ) : (
                                        <div className={style.fallbackWrapper}>
                                            {fallbackImage?.imageSrc()}
                                        </div>
                                    )}
                                    <CardBody className={style.cardBody}>
                                        <div className={style.cardInformation}>
                                            <Typography
                                                type="heading-6-bold"
                                                className={style.typographyName}
                                            >
                                                {employee.firstName}{" "}
                                                {employee.lastName}
                                            </Typography>
                                            <Typography
                                                type="heading-6-bold"
                                                className={
                                                    style.typographyFunction
                                                }
                                            >
                                                {employee.functions
                                                    .map((func) =>
                                                        locale === "nl"
                                                            ? func.functionNameNl
                                                            : func.functionNameFr,
                                                    )
                                                    .filter(
                                                        (name) =>
                                                            typeof name ===
                                                            "string",
                                                    )
                                                    .join(" - ")}
                                            </Typography>
                                        </div>
                                        <ContactInfo
                                            email={employee.email}
                                            telephone={employee.telephone}
                                        />
                                    </CardBody>
                                </Card>
                            ))}
                        </AgencyTeamCarousel>
                    </div>
                </Col>
            )}
        </Section>
    );
};
export default AgencyTeam;
